import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  Calendar,
  FilePlus,
  Mail,
  MoreVertical,
  Phone,
} from 'lucide-react'
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon'
import { Button } from '@/components/ui/button'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ClientStatusTagBadge,
  ClientTypeTagBadge,
  ProspectBadge,
} from '@/components/shared/StatusBadges'
import { useQuickActions } from '@/hooks/useQuickActions'
import { usePermissions } from '@/hooks/usePermissions'
import { formatCurrency, formatPostalAddress } from '@/lib/format'
import { formatIsoDateDisplay } from '@/lib/date'
import { primaryEmail, primaryPhone } from '@/lib/clientContacts'
import { cn } from '@/lib/utils'
import type { ClientDetail, ClientType } from '@/types'
import euroDangerIcon from '@/assets/clients/detail/euro-danger.svg'
import euroPositiveIcon from '@/assets/clients/detail/euro-positive.svg'
import clockIcon from '@/assets/clients/detail/clock.svg'
import fileIcon from '@/assets/clients/detail/file.svg'
import calendarIcon from '@/assets/clients/detail/calendar.svg'
import briefcaseIcon from '@/assets/clients/detail/briefcase.svg'
import editProfileIcon from '@/assets/clients/actions/edit-profile.svg'
import trashIcon from '@/assets/clients/actions/trash.svg'

const cardShadow =
  'shadow-[0px_8px_16px_-4px_rgba(38,45,66,0.1),0px_1px_4px_-4px_rgba(38,45,66,0.05)]'

// Figma 2881:34146 / 2883:34653 — same menu chrome as ClientsListPage.
const clientMenuContentClass =
  'min-w-[200px] rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0px_1px_4px_-1px_rgba(38,45,66,0.05),0px_1px_8px_-1px_rgba(38,45,66,0.1)]'
const clientMenuItemClass =
  'cursor-pointer gap-3 rounded-lg px-4 py-3 text-base font-normal leading-[1.4] text-[#171a20] focus:bg-[#f4f5f8] focus:text-[#171a20]'
const clientMenuItemDestructiveClass =
  'cursor-pointer gap-3 rounded-lg px-4 py-3 text-base font-normal leading-[1.4] text-[#d63b33] focus:bg-[#f4f5f8] focus:text-[#d63b33]'
const moreActionsTriggerClass =
  'flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-transparent bg-white p-[9px] text-[#171a20] transition-colors hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] data-[state=open]:bg-[#ffe2d2] data-[state=open]:hover:bg-[#ffe2d2]'

function typeLabelKey(type: ClientType | string | undefined): string {
  if (type === 'individual') return 'list.typeLabels.individual'
  if (type === 'property_management') return 'list.typeLabels.propertyManager'
  return 'list.typeLabels.business'
}

function formatCustomerSince(createdAt: string, language: string): string {
  const d = new Date(createdAt)
  if (Number.isNaN(d.getTime())) return ''
  const locale = language === 'fr' ? 'fr-FR' : 'en-GB'
  return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
}

function IconActionButton({
  label,
  onClick,
  className,
  children,
}: {
  label: string
  onClick?: () => void
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type='button'
      aria-label={label}
      onClick={onClick}
      className={cn(
        'flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-transparent bg-white p-[9px] text-[#171a20] transition-colors hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]',
        className
      )}
    >
      {children}
    </button>
  )
}

function SoftBadge({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center justify-center gap-1 rounded-lg px-2 py-1 text-xs font-medium leading-4 whitespace-nowrap',
        className
      )}
    >
      {children}
    </span>
  )
}

function MetricCell({
  iconSrc,
  iconClass,
  iconBoxClass,
  label,
  children,
  showDivider,
}: {
  iconSrc: string
  iconClass: string
  iconBoxClass: string
  label: string
  children: ReactNode
  showDivider?: boolean
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-start gap-3',
        showDivider && 'lg:border-r lg:border-[#b0b5c3] lg:pr-6'
      )}
    >
      <span
        className={cn(
          'flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-xl p-3',
          iconBoxClass
        )}
      >
        <img
          src={iconSrc}
          alt=''
          className={cn('block max-w-none', iconClass)}
          width={20}
          height={20}
        />
      </span>
      <div className='flex min-w-0 flex-1 flex-col gap-1'>
        <p className='text-xs font-semibold uppercase leading-[1.4] text-[#3d434e]'>
          {label}
        </p>
        {children}
      </div>
    </div>
  )
}

// Client detail info header (Figma 2889:56660) — replaces the former sidebar.
export function ClientDetailInfoSection({
  client,
  canDeleteClient,
  canEditClient,
  onDelete,
}: {
  client: ClientDetail
  canDeleteClient: boolean
  canEditClient: boolean
  onDelete: () => void
}) {
  const { t, i18n } = useTranslation('clients')
  const navigate = useNavigate()
  const { canEdit, canViewPath } = usePermissions()
  const { callClient, emailClient, whatsappClient, navigateToAddress } =
    useQuickActions()

  const clientInitial = client.name?.trim().charAt(0).toUpperCase() || '?'
  const address = formatPostalAddress(client)
  const sourceLabel = client.source
    ? t(`sourceLabels.${client.source.toLowerCase()}`, {
        defaultValue: client.source.replace(/_/g, ' '),
      })
    : null
  const typeLabel = t(typeLabelKey(client.type))
  const statusLabel = t(
    `common:status.${(client.status ?? 'active').toLowerCase()}`,
    { defaultValue: client.status ?? 'active' }
  )
  const customerSince = formatCustomerSince(client.created_at, i18n.language)

  const collectAmount =
    client.open_invoices_total ?? client.outstanding_receivables ?? 0
  const hasAmountToCollect = collectAmount > 0
  const chaseInvoiceId = client.chase?.invoice_id ?? null
  const turnover = client.total_revenue ?? 0
  const activeContracts = client.active_contracts_count ?? 0
  const interventionsCount = client.interventions_count ?? 0
  const nextVisitDate = client.next_visit_date ?? null
  const canCreateQuote = canEdit('quotes')
  const canCreateJob = canEdit('agenda')
  const canViewChaseInvoice = chaseInvoiceId
    ? canViewPath(`/invoices/${chaseInvoiceId}`)
    : false
  const canCollectNavigate = canViewChaseInvoice || canViewPath('/invoices')

  const handleCollectClick = () => {
    if (chaseInvoiceId && canViewChaseInvoice) {
      navigate(`/invoices/${chaseInvoiceId}`)
      return
    }
    if (canViewPath('/invoices')) {
      navigate(`/clients/${client.id}?tab=invoices`)
    }
  }

  return (
    <section
      className={cn(
        'flex w-full flex-col gap-4 overflow-hidden rounded-2xl border border-[#e7e9ef] p-6',
        cardShadow
      )}
      style={{
        backgroundImage:
          'linear-gradient(90deg, #ffffff 0%, #fffcfa 30%, #fff8f4 65%, #fff3ec 100%)',
      }}
    >
      {/* Identity + actions */}
      <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
        <div className='flex min-w-0 flex-1 flex-wrap items-start gap-4'>
          <div className='flex items-center gap-3'>
            <span className='flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#ff5a1f] font-heading text-base font-semibold leading-[1.2] text-white'>
              {clientInitial}
            </span>
            <div className='flex min-w-0 flex-col gap-0.5'>
              <div className='flex min-w-0 items-center gap-1'>
                <p className='font-heading text-xl font-semibold leading-[1.2] text-[#171a20]'>
                  {client.name}
                </p>
                <ProspectBadge category={client.category} />
              </div>
              <p className='text-xs leading-[1.4] text-[#3d434e]'>
                {t('detail.idLabel', {
                  id: client.reference?.trim() || '—',
                })}
              </p>
            </div>
          </div>

          <div className='flex flex-wrap items-center gap-2'>
            <ClientStatusTagBadge
              status={client.status ?? 'active'}
              label={statusLabel}
            />
            {client.type === 'company' ? (
              <SoftBadge className='bg-[#efdbff] text-[#531dab]'>
                <img
                  src={briefcaseIcon}
                  alt=''
                  className='size-3.5 max-w-none'
                  width={14}
                  height={14}
                />
                {typeLabel}
              </SoftBadge>
            ) : (
              <ClientTypeTagBadge type={client.type} label={typeLabel} />
            )}
            {sourceLabel && (
              <SoftBadge className='bg-[#e7e9ef] text-[#171a20]'>
                {sourceLabel}
              </SoftBadge>
            )}
          </div>
        </div>

        <div className='flex flex-wrap items-center gap-3'>
          {canCreateQuote && (
            <Button
              type='button'
              className='h-10 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 py-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'
              onClick={() => navigate(`/quotes/new?client_id=${client.id}`)}
            >
              <FilePlus className='size-4' />
              {t('detail.newQuote')}
            </Button>
          )}
          {canCreateJob && (
            <Button
              type='button'
              variant='outline'
              className='h-10 gap-2 rounded-xl border-[#ff5a1f] bg-white px-3 py-3 text-sm font-medium leading-none text-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
              onClick={() => navigate(`/jobs/new?client_id=${client.id}`)}
            >
              <Calendar className='size-4' />
              {t('detail.newIntervention')}
            </Button>
          )}
          <TruncatedTextTooltip
            alwaysShow
            closeOnPointerDown
            className='inline-flex shrink-0'
            content={t('list.actions.call', { name: client.name })}
            delayDuration={0}
          >
            <IconActionButton
              label={t('list.actions.call', { name: client.name })}
              onClick={() => callClient(primaryPhone(client))}
            >
              <Phone className='size-4' />
            </IconActionButton>
          </TruncatedTextTooltip>
          <TruncatedTextTooltip
            alwaysShow
            closeOnPointerDown
            className='inline-flex shrink-0'
            content={t('list.actions.email', { name: client.name })}
            delayDuration={0}
          >
            <IconActionButton
              label={t('list.actions.email', { name: client.name })}
              onClick={() => emailClient(primaryEmail(client))}
            >
              <Mail className='size-4' />
            </IconActionButton>
          </TruncatedTextTooltip>
          <TruncatedTextTooltip
            alwaysShow
            closeOnPointerDown
            className='inline-flex shrink-0'
            content={t('list.actions.whatsapp')}
            delayDuration={0}
          >
            <IconActionButton
              label={t('list.actions.whatsapp')}
              className='border-[#25d366] bg-[#25d366] text-white hover:border-[#25d366] hover:bg-[#25d366] hover:text-white [&_path]:fill-white'
              onClick={() => whatsappClient(primaryPhone(client))}
            >
              <WhatsAppIcon width={16} height={16} />
            </IconActionButton>
          </TruncatedTextTooltip>
          {(canEditClient || canDeleteClient) && (
            <DropdownMenu>
              <TruncatedTextTooltip
                alwaysShow
                closeOnPointerDown
                className='inline-flex shrink-0'
                content={t('list.actions.moreActions')}
                delayDuration={0}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    type='button'
                    aria-label={t('list.actions.moreActions')}
                    className={moreActionsTriggerClass}
                  >
                    <MoreVertical className='size-4'/>
                  </button>
                </DropdownMenuTrigger>
              </TruncatedTextTooltip>
              <DropdownMenuContent
                align='end'
                sideOffset={8}
                className={clientMenuContentClass}
              >
                {canEditClient && (
                  <DropdownMenuItem
                    className={clientMenuItemClass}
                    onClick={() => navigate(`/clients/${client.id}/edit`)}
                  >
                    <img
                      src={editProfileIcon}
                      alt=''
                      width={16}
                      height={16}
                      className='size-4 shrink-0'
                    />
                    {t('detail.edit')}
                  </DropdownMenuItem>
                )}
                {canDeleteClient && (
                  <DropdownMenuItem
                    className={clientMenuItemDestructiveClass}
                    onClick={onDelete}
                  >
                    <img
                      src={trashIcon}
                      alt=''
                      width={16}
                      height={16}
                      className='size-4 shrink-0'
                    />
                    {t('detail.delete')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Address + tenure + metrics */}
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2 border-b border-dashed border-[#b0b5c3] pb-4'>
          <div className='flex flex-wrap items-center gap-1'>
            {address ? (
              <p className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                {address}
              </p>
            ) : (
              <p className='text-sm font-medium leading-[1.4] text-[#757575]'>
                —
              </p>
            )}
            <span
              className='mx-1 size-1 shrink-0 rounded-full bg-[#b0b5c3]'
              aria-hidden
            />
            <button
              type='button'
              className='inline-flex items-center gap-2 rounded-xl px-1 py-0.5 text-sm font-medium leading-none text-[#ff5a1f] transition-colors hover:text-[#e6511c]'
              onClick={() =>
                navigateToAddress({
                  address: address || null,
                  latitude: client.latitude,
                  longitude: client.longitude,
                })
              }
            >
              {t('detail.direction')}
              <ArrowRight className='size-4' />
            </button>
          </div>

          <div className='flex flex-wrap items-center gap-2'>
            {customerSince && (
              <SoftBadge className='bg-[#d1fae5] text-[#065f46]'>
                {t('detail.customerSince', { period: customerSince })}
              </SoftBadge>
            )}
          </div>
        </div>

        <div className='flex flex-col gap-4 lg:flex-row lg:gap-6'>
          {hasAmountToCollect && (
            <MetricCell
              iconSrc={euroDangerIcon}
              iconClass='h-[17.84px] w-[18.83px]'
              iconBoxClass='bg-[#fde9e8]'
              label={t('detail.metrics.collectMoney')}
              showDivider
            >
              <div className='flex flex-wrap items-center gap-2'>
                <p className='font-heading text-base font-bold leading-[1.2] text-[#d63b33]'>
                  {formatCurrency(collectAmount, 'EUR', 2)}
                </p>
                <Button
                  type='button'
                  className='h-6 shrink-0 gap-1 rounded-lg px-2 py-0 text-sm font-medium leading-[1.2]'
                  onClick={handleCollectClick}
                  disabled={!canCollectNavigate}
                >
                  {t('chaseInvoice.collectNow')}
                  <ArrowRight className='size-3.5 shrink-0' />
                </Button>
              </div>
            </MetricCell>
          )}

          <MetricCell
            iconSrc={euroPositiveIcon}
            iconClass='h-[17.84px] w-[18.83px]'
            iconBoxClass='bg-[#ecfdf5]'
            label={t('detail.metrics.totalTurnover')}
            showDivider
          >
            <p className='font-heading text-base font-bold leading-[1.2] text-[#171a20]'>
              {formatCurrency(turnover, 'EUR', 2)}
            </p>
          </MetricCell>

          <MetricCell
            iconSrc={clockIcon}
            iconClass='size-[18.67px]'
            iconBoxClass='bg-[#f9f0ff]'
            label={t('detail.metrics.interventions')}
            showDivider
          >
            {interventionsCount > 0 ? (
              <p className='font-heading text-base font-bold leading-[1.2] text-[#171a20]'>
                {interventionsCount}
              </p>
            ) : (
              <div className='flex flex-wrap items-center gap-3'>
                <p className='font-heading text-base font-bold leading-[1.2] text-[#171a20]'>
                  0
                </p>
                <p className='text-xs font-medium leading-[1.4] text-[#3d434e]'>
                  {t('detail.metrics.noneSoFar')}
                </p>
              </div>
            )}
          </MetricCell>

          <MetricCell
            iconSrc={fileIcon}
            iconClass='h-[18.67px] w-[15.33px]'
            iconBoxClass='bg-[#fcf1fd]'
            label={t('detail.metrics.contract')}
            showDivider
          >
            {activeContracts > 0 ? (
              <p className='font-heading text-base font-bold leading-[1.2] text-[#171a20]'>
                {activeContracts}
              </p>
            ) : (
              <div className='flex flex-wrap items-center gap-3'>
                <p className='font-heading text-base font-bold leading-[1.2] text-[#171a20]'>
                  —
                </p>
                <p className='text-xs font-medium leading-[1.4] text-[#3d434e]'>
                  {t('detail.metrics.noActiveContracts')}
                </p>
              </div>
            )}
          </MetricCell>

          <MetricCell
            iconSrc={calendarIcon}
            iconClass='size-[18.67px]'
            iconBoxClass='bg-[#fff2e8]'
            label={t('detail.metrics.nextVisit')}
          >
            {nextVisitDate ? (
              <p className='font-heading text-base font-bold leading-[1.2] text-[#171a20]'>
                {formatIsoDateDisplay(nextVisitDate)}
              </p>
            ) : (
              <div className='flex flex-wrap items-center gap-3'>
                <p className='font-heading text-base font-bold leading-[1.2] text-[#171a20]'>
                  —
                </p>
                <p className='text-xs font-medium leading-[1.4] text-[#3d434e]'>
                  {t('detail.metrics.nothingPlanned')}
                </p>
              </div>
            )}
          </MetricCell>
        </div>
      </div>
    </section>
  )
}
